import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import ProfileSection from '../components/Settings/ProfileSection';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaletteIcon from '@mui/icons-material/Palette';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { useNotification } from '../contexts/NotificationContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/** Your Settings — profile, notification preferences, appearance. `embedded`: inside the tabbed Settings page (P4-01). */
function Settings({ embedded = false }) {
  const { roles } = useUser();
  // FLS / DH default ON; everyone else gets the daily digest only (P2-33, C0820)
  const conditionInstantDefault = Object.values(roles || {}).some((r) => ['team_lead', 'department_lead'].includes(r?.role));
  const { user } = useAuth();
  const { profile } = useUser();
  const { notify } = useNotification();
  const { themeMode, toggleTheme } = useThemeMode();


  // Notification preferences — these two are LIVE: the n8n notification
  // workflows (WF8b instant alerts, WF9 morning digest) read them before
  // sending. Defaults ON for the beta.
  const [notifPrefs, setNotifPrefs] = useState({
    instantAlerts: true,
    dailyDigest: true,
    // P2-33: instant emails per OE Condition event (FLS/DH default on; site managers and above get the digest)
    conditionInstant: null,
  });
  const [prefsLoading, setPrefsLoading] = useState(true);

  // Load notification preferences from Firestore
  useEffect(() => {
    if (!user) return;
    const loadPrefs = async () => {
      try {
        const prefsDoc = await getDoc(doc(db, 'Hugo_UserPreferences', user.uid));
        if (prefsDoc.exists()) {
          const data = prefsDoc.data();
          if (data.notifications) {
            setNotifPrefs((prev) => ({ ...prev, ...data.notifications }));
          }
        }
      } catch (err) {
        console.warn('Could not load notification preferences:', err.message);
      } finally {
        setPrefsLoading(false);
      }
    };
    loadPrefs();
  }, [user]);

  const handleNotifToggle = (key, fallback) => async () => {
    const current = notifPrefs[key] == null && fallback !== undefined ? fallback : notifPrefs[key];
    const newPrefs = { ...notifPrefs, [key]: !current };
    setNotifPrefs(newPrefs);
    try {
      await setDoc(
        doc(db, 'Hugo_UserPreferences', user.uid),
        { notifications: newPrefs, updatedAt: new Date() },
        { merge: true }
      );
      notify('Notification preferences updated', 'success');
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      // Revert on failure
      setNotifPrefs(notifPrefs);
      notify('Failed to update preferences', 'error');
    }
  };

  const handleThemeToggle = () => {
    toggleTheme();
    notify(
      `Switched to ${themeMode === 'light' ? 'dark' : 'light'} mode`,
      'info'
    );
  };

  return (
    <Box>
      {!embedded && (
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
      )}

      {/* Profile Section (P4-27) */}
      <ProfileSection />

      {/* Notification Preferences Section */}
      <Card sx={{ maxWidth: 600, mt: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <NotificationsIcon color="primary" />
            <Typography variant="h6">Notification Preferences</Typography>
          </Stack>
          {prefsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifPrefs.instantAlerts}
                    onChange={handleNotifToggle('instantAlerts')}
                    aria-label="Instant alert emails toggle"
                  />
                }
                label="Instant alert emails"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                Email the moment a warning or critical risk alert fires in your organization —
                with a link straight to the affected dashboard
              </Typography>
              <Divider />
              <FormControlLabel
                control={
                  <Switch
                    checked={notifPrefs.dailyDigest}
                    onChange={handleNotifToggle('dailyDigest')}
                    aria-label="Morning summary toggle"
                  />
                }
                label="Morning summary"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                One short email each morning: your current standing, open warnings, and
                anything waiting on you — brief and calm on quiet days.
              </Typography>
              <Divider />
              <FormControlLabel
                control={
                  <Switch
                    checked={notifPrefs.conditionInstant == null ? conditionInstantDefault : Boolean(notifPrefs.conditionInstant)}
                    onChange={handleNotifToggle('conditionInstant', conditionInstantDefault)}
                    aria-label="Instant OE Condition emails toggle"
                  />
                }
                label="Instant OE Condition emails"
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                An email each time an OE Condition needs you — new items in your review queue, returns, resolutions
                to approve, conditions assigned to you. Off = you still see everything in the bell and in the morning
                summary (the default for Facility/Site/Plant Managers and above).
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Theme Section */}
      <Card sx={{ maxWidth: 600, mt: 3, mb: 4 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <PaletteIcon color="primary" />
            <Typography variant="h6">Appearance</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={themeMode === 'dark'}
                  onChange={handleThemeToggle}
                  aria-label="Dark mode toggle"
                />
              }
              label={themeMode === 'dark' ? 'Dark Mode' : 'Light Mode'}
            />
            {themeMode === 'dark' ? (
              <Brightness4Icon color="action" />
            ) : (
              <Brightness7Icon color="action" />
            )}
            <Chip
              label={themeMode === 'dark' ? 'Dark' : 'Light'}
              size="small"
              color={themeMode === 'dark' ? 'default' : 'primary'}
              variant="outlined"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Theme preference is saved automatically and persists across sessions.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Settings;
